import { useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Upload,
  Image as ImageIcon,
  CheckCircle,
  AlertCircle,
  Loader2,
  LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadSessionId: string;
  onUploadComplete?: (uploadedImages: UploadedImage[]) => void;
  existingImages?: UploadedImage[];
}

export interface ImagePreview {
  id: string;
  file: File;
  preview: string;
  width: number;
  height: number;
  isValid: boolean;
  error?: string;
}

export interface UploadedImage {
  id: string;
  file_name: string;
  file_path: string;
  width: number;
  height: number;
}

const MIN_IMAGES = 15;
const MAX_IMAGES = 40;
const MIN_FILE_SIZE = 500 * 1024; // 500KB
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MIN_WIDTH = 800;
const MIN_HEIGHT = 800;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/heic"];

export function ImageUploadModal({
  isOpen,
  onClose,
  uploadSessionId,
  onUploadComplete,
  existingImages = [],
}: ImageUploadModalProps) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const remainingSlots = MAX_IMAGES - existingImages.length;
  const isAuthenticated = !!user;

  const validateImage = async (file: File): Promise<ImagePreview> => {
    return new Promise((resolve) => {
      const id = crypto.randomUUID();

      // Check file type
      if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
        resolve({
          id,
          file,
          preview: "",
          width: 0,
          height: 0,
          isValid: false,
          error: "Only JPG, PNG, and HEIC files are allowed",
        });
        return;
      }

      // Check file size
      if (file.size < MIN_FILE_SIZE) {
        resolve({
          id,
          file,
          preview: "",
          width: 0,
          height: 0,
          isValid: false,
          error: "File size must be at least 500KB",
        });
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        resolve({
          id,
          file,
          preview: "",
          width: 0,
          height: 0,
          isValid: false,
          error: "File size must be less than 10MB",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
            resolve({
              id,
              file,
              preview: "",
              width: img.width,
              height: img.height,
              isValid: false,
              error: `Image must be at least ${MIN_WIDTH}×${MIN_HEIGHT}px (yours is ${img.width}×${img.height}px)`,
            });
            return;
          }
          resolve({
            id,
            file,
            preview: e.target?.result as string,
            width: img.width,
            height: img.height,
            isValid: true,
          });
        };
        img.onerror = () => {
          resolve({
            id,
            file,
            preview: "",
            width: 0,
            height: 0,
            isValid: false,
            error: "Failed to load image",
          });
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    // Limit to remaining slots
    const filesToProcess = fileArray.slice(
      0,
      remainingSlots - images.filter((i) => i.isValid).length
    );

    const validatedImages = await Promise.all(
      filesToProcess.map((file) => validateImage(file))
    );

    setImages((prev) => [...prev, ...validatedImages]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [remainingSlots, images]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // Early return AFTER all hooks
  if (!isOpen) return null;

  // Handle login redirect
  const handleLoginRedirect = (mode: "login" | "signup" = "signup") => {
    const currentUrl = window.location.href;
    sessionStorage.setItem("redirectAfterLogin", currentUrl);
    navigate(mode === "signup" ? "/signup" : "/login");
  };

  // Show login prompt if not authenticated - but try refreshing session first
  if (!isAuthenticated && !authLoading) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-primary" />
              </div>
               <h2 className="font-heading text-xl font-bold text-foreground mb-2">
                 Sign In Required
               </h2>
               <p className="text-muted-foreground mb-6">
                 Please sign in to upload your photos. Your images will be securely stored in your account.
               </p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={() => handleLoginRedirect("signup")}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  const removeImage = (id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const handleUpload = async () => {
    const validImages = images.filter((img) => img.isValid);
    const totalImages = existingImages.length + validImages.length;

    if (totalImages < MIN_IMAGES) {
      toast({
        title: "Not enough images",
        description: `Please upload at least ${MIN_IMAGES - existingImages.length} valid images`,
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const uploadedImages: UploadedImage[] = [];
      let uploadedCount = 0;

      for (const imagePreview of validImages) {
        const file = imagePreview.file;
        const fileExt = file.name.split(".").pop();
        const fileName = `${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${user.id}/${uploadSessionId}/${fileName}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from("customer-photos")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw uploadError;
        }

        // Save metadata to database
        const { data: dbData, error: dbError } = await supabase
          .from("image_uploads")
          .insert({
            user_id: user.id,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            width: imagePreview.width,
            height: imagePreview.height,
            mime_type: file.type,
            upload_session_id: uploadSessionId,
            status: "pending",
          })
          .select()
          .single();

        if (dbError) {
          console.error("Database error:", dbError);
          throw dbError;
        }

        uploadedImages.push({
          id: dbData.id,
          file_name: file.name,
          file_path: filePath,
          width: imagePreview.width,
          height: imagePreview.height,
        });

        uploadedCount++;
        setUploadProgress((uploadedCount / validImages.length) * 100);
      }

      toast({
        title: "Upload complete!",
        description: `Successfully uploaded ${uploadedCount} images.`,
      });

      if (onUploadComplete) {
        onUploadComplete(uploadedImages);
      }

      setImages([]);
      onClose();
    } catch (err: any) {
      console.error("Upload failed:", err);
      toast({
        title: "Upload failed",
        description: err.message || "Failed to upload images. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setImages([]);
      onClose();
    }
  };

  const validCount = images.filter((img) => img.isValid).length;
  const invalidCount = images.length - validCount;
  const totalValidCount = existingImages.length + validCount;
  const canUpload = totalValidCount >= MIN_IMAGES && validCount > 0 && !uploading;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-card rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">
                Upload Photos
              </h2>
              <p className="text-sm text-muted-foreground">
                Upload {MIN_IMAGES}-{MAX_IMAGES} images (500KB–10MB each)
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={uploading}
              aria-label="Close upload dialog"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                dragActive
                  ? "border-primary bg-primary/10"
                  : uploading
                  ? "border-muted bg-muted/50 cursor-not-allowed"
                  : "border-primary/30 hover:border-primary hover:bg-primary/5"
              }`}
            >
              <Upload className="w-10 h-10 mx-auto mb-3 text-primary/60" />
              <p className="font-medium text-foreground mb-1">
                {uploading ? "Uploading..." : "Click to select or drag and drop"}
              </p>
              <p className="text-sm text-muted-foreground">
                JPG, PNG, or HEIC • 500KB–10MB • Min 800×800px
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />

            {/* Status Bar */}
            {(images.length > 0 || existingImages.length > 0) && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {existingImages.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {existingImages.length} already uploaded
                      </span>
                    )}
                    {validCount > 0 && (
                      <span className="flex items-center gap-1 text-sm text-primary">
                        <CheckCircle className="w-4 h-4" />
                        {validCount} ready to upload
                      </span>
                    )}
                    {invalidCount > 0 && (
                      <span className="flex items-center gap-1 text-sm text-destructive">
                        <AlertCircle className="w-4 h-4" />
                        {invalidCount} invalid
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium">
                    {totalValidCount < MIN_IMAGES
                      ? `${MIN_IMAGES - totalValidCount} more needed`
                      : "✓ Minimum met"}
                  </span>
                </div>

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Uploading images...</span>
                      <span className="font-medium">{Math.round(uploadProgress)}%</span>
                    </div>
                    <Progress value={uploadProgress} />
                  </div>
                )}
              </div>
            )}

            {/* Image Preview Grid */}
            <div className="min-h-[200px]">
              {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                  <p>No images selected yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {images.map((image) => (
                    <div
                      key={image.id}
                      className={`relative aspect-square rounded-lg overflow-hidden group border-2 ${
                        image.isValid ? "border-primary/30" : "border-destructive"
                      }`}
                    >
                      {image.preview ? (
                        <img
                          src={image.preview}
                          alt={image.file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}

                      {/* Status Badge */}
                      <div className="absolute bottom-1 left-1 z-10">
                        {image.isValid ? (
                          <span className="inline-flex items-center gap-1 bg-primary/90 text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {image.width}×{image.height}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground text-xs px-2 py-0.5 rounded-full">
                            <AlertCircle className="w-3 h-3" />
                            Invalid
                          </span>
                        )}
                      </div>

                      {/* Remove Button - always visible (mobile friendly) */}
                      <button
                        type="button"
                        onClick={() => removeImage(image.id)}
                        disabled={uploading}
                        className={`absolute top-1 right-1 z-20 p-1.5 rounded-full disabled:opacity-50 transition-colors ${
                          image.isValid
                            ? "opacity-100 bg-background/80 text-foreground hover:bg-destructive hover:text-destructive-foreground"
                            : "opacity-100 bg-destructive text-destructive-foreground"
                        }`}
                        title="Remove image"
                        aria-label={`Remove ${image.file.name}`}
                      >
                        <X className="w-4 h-4" />
                      </button>

                      {/* Error Tooltip */}
                      {image.error && (
                        <div className="pointer-events-none absolute inset-0 bg-destructive/80 flex items-center justify-center p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-xs text-destructive-foreground text-center">
                            {image.error}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {totalValidCount >= MIN_IMAGES ? (
                  <span className="text-primary font-medium">
                    ✓ Ready to upload {validCount} images
                  </span>
                ) : (
                  `Select at least ${MIN_IMAGES - totalValidCount} valid images to continue`
                )}
              </p>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={handleClose} disabled={uploading}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={!canUpload}>
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    `Upload ${validCount} Images`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ImageUploadModal;
